import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { useOnEscapePress } from '@/hooks/useOnEscapePress';
import { Button, IconButton } from '@/ui/Button';
import { LinearProgressBar } from '@/ui/ProgressBar';

const CONTENT_MAX_WIDTH = 600;

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${(p) => p.theme.colors.neutral.background};
`;

const HeaderStyled = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 75px;
  padding: 0 24px;
`;

/** Holds the close button on the right when there is nothing to go back to. */
const HeaderSpacerStyled = styled.div`
  flex: 1;
`;

/**
 * `LinearProgressBar` renders no aria of its own, so the wrapper carries the role and the
 * values. It reports completed steps, which is what the filled portion of the bar shows.
 */
const ProgressStyled = styled.div`
  width: 100%;
`;

const ContentStyled = styled.main`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const ContentColumnStyled = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH}px;
  padding: 40px 24px 84px;
`;

const FooterStyled = styled.footer`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding: 15px 24px 14px;
  border-top: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  background-color: ${(p) => p.theme.colors.neutral.background};
`;

/**
 * The footer's action spans the column. The rule lives here rather than in a `styled(Button)` on
 * each page because styled-components consumes transient `$`-prefixed props and never forwards
 * them to a wrapped component, so such a wrapper silently swallows `Button`'s `$loading` — the
 * action then neither spins nor disables, and the step's request can be fired repeatedly.
 */
const FooterColumnStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH}px;

  > * {
    width: 100%;
  }
`;

type CsvImportWizardLayoutProps = {
  /** Number of steps the flow has, i.e. the number of segments in the progress bar. */
  stepCount: number;
  /** Zero-based index of the step on screen. Step 0 shows an empty progress bar. */
  activeStep: number;
  onClose: VoidFunction;
  /** Omit on the first step, where there is nowhere to go back to. */
  onBack?: VoidFunction;
  children: ReactNode;
  /** Pinned to the bottom of the viewport, typically the step's primary action. */
  footer?: ReactNode;
  /** Accessible name of the progress bar, e.g. "Invite users progress". */
  progressLabel?: string;
  className?: string;
};

/**
 * Full-screen chrome for a CSV import wizard: a step's content in a narrow centred column,
 * progress across the top and the step's action pinned to the bottom. Shared by the bulk
 * invite-users flow and the bulk add-learners-to-a-journey flow.
 *
 * The wizard is a route rather than a `Modal`, so nothing above it dismisses it — Escape is
 * wired here, and does exactly what the close button does.
 */
export const CsvImportWizardLayout = ({
  stepCount,
  activeStep,
  onClose,
  onBack,
  children,
  footer,
  progressLabel,
  className,
}: CsvImportWizardLayoutProps): JSX.Element => {
  useOnEscapePress(onClose, [onClose]);

  return (
    <RootStyled className={className}>
      <HeaderStyled>
        {onBack ? (
          <Button variant="ghost" icon="arrow-left" onClick={onBack}>
            Back
          </Button>
        ) : (
          <HeaderSpacerStyled />
        )}
        <IconButton icon="xmark" buttonVariant="buddySmall" onClick={onClose} aria-label="Close" />
      </HeaderStyled>
      <ProgressStyled
        role="progressbar"
        aria-label={progressLabel}
        aria-valuemin={0}
        aria-valuemax={stepCount}
        aria-valuenow={activeStep}
        aria-valuetext={`${activeStep} of ${stepCount} steps completed`}
      >
        <LinearProgressBar value={activeStep} maxValue={stepCount} size="xs" roundedBar={false} roundedTrack={false} />
      </ProgressStyled>
      <ContentStyled>
        <ContentColumnStyled>{children}</ContentColumnStyled>
      </ContentStyled>
      {!!footer && (
        <FooterStyled>
          <FooterColumnStyled>{footer}</FooterColumnStyled>
        </FooterStyled>
      )}
    </RootStyled>
  );
};
